import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import ProjectAccessModal from '../components/ProjectAccessModal';
import { getApiUrl } from '../utils/apiConfig';
import { Video } from '../types';
import { getVideoStatusClasses, getVideoStatusLabel } from '../utils/colorUtils';

interface Project {
  id: string;
  name: string;
  description?: string;
  videos: Video[];
}

const PublicProject: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessGranted, setAccessGranted] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // États pour l'ajout/modification de vidéo
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState<Video | null>(null);
  const [videoMode, setVideoMode] = useState<'youtube' | 'upload'>('youtube');
  const [videoForm, setVideoForm] = useState({
    title: '',
    youtubeUrl: '',
    description: '',
    version: 1,
    status: 'READY'
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // États pour la confirmation de suppression
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<string | null>(null);

  const handleAccess = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Récupérer le mot de passe sauvegardé
      const savedPassword = localStorage.getItem(`project_${projectId}_password`);
      if (!savedPassword) {
        throw new Error('Aucun mot de passe sauvegardé');
      }

      const response = await fetch(getApiUrl(`/projects/${projectId}/public?password=${savedPassword}`));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Accès refusé');
      }

      setProject(data);
      setAccessGranted(true);
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    // Vérifier si c'est un admin
    const adminToken = localStorage.getItem('admin_token');
    setIsAdmin(!!adminToken);

    // Vérifier si on a accès au projet
    const savedPassword = localStorage.getItem(`project_${projectId}_password`);
    if (savedPassword) {
      handleAccess();
    }
  }, [projectId, handleAccess]);

  const handleAccessGranted = () => {
    // Recharger le projet après l'accès accordé
    handleAccess();
  };

  // Fonctions pour gérer les vidéos (admin seulement)
  const handleAddVideo = () => {
    setEditingVideo(null);
    setVideoMode('youtube');
    setSelectedFile(null);
    setVideoForm({ title: '', youtubeUrl: '', description: '', version: 1, status: 'READY' });
    setShowVideoForm(true);
  };

  const handleEditVideo = (video: Video) => {
    setEditingVideo(video);
    setVideoMode(video.storageType === 'YOUTUBE' ? 'youtube' : 'upload');
    setSelectedFile(null);
    setVideoForm({
      title: video.title,
      youtubeUrl: video.youtubeUrl || '',
      description: video.description || '',
      version: video.version,
      status: video.status || 'READY'
    });
    setShowVideoForm(true);
  };

  const handleDeleteVideo = (videoId: string) => {
    setVideoToDelete(videoId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteVideo = async () => {
    if (!videoToDelete) return;

    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await fetch(getApiUrl(`/videos/${videoToDelete}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      // Recharger le projet
      handleAccess();
      setShowDeleteConfirm(false);
      setVideoToDelete(null);
    } catch (err: any) {
      alert('Erreur lors de la suppression: ' + err.message);
      setShowDeleteConfirm(false);
      setVideoToDelete(null);
    }
  };

  const handleVideoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const adminToken = localStorage.getItem('admin_token');
      const method = editingVideo ? 'PUT' : 'POST';

      // Pour l'édition, on utilise toujours l'ancien endpoint
      const url = editingVideo
        ? getApiUrl(`/videos/${editingVideo.id}`)
        : getApiUrl('/videos/create'); // Nouveau endpoint flexible

      let response: Response;

      if (editingVideo) {
        // Édition : toujours JSON
        const body = {
          ...(videoForm.title !== undefined && { title: videoForm.title }),
          ...(videoForm.description !== undefined && { description: videoForm.description }),
          ...(videoForm.version !== undefined && { version: videoForm.version }),
          ...(videoForm.status !== undefined && videoForm.status !== '' && { status: videoForm.status })
        };

        response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`,
          },
          body: JSON.stringify(body),
        });
      } else {
        // Création : selon le mode choisi
        if (videoMode === 'youtube') {
          // Mode YouTube : JSON avec URL
          const body = {
            title: videoForm.title,
            description: videoForm.description,
            youtubeUrl: videoForm.youtubeUrl,
            projectId: projectId
          };

          response = await fetch(url, {
            method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${adminToken}`,
            },
            body: JSON.stringify(body),
          });
        } else {
          // Mode upload : FormData avec fichier
          if (!selectedFile) {
            throw new Error('Veuillez sélectionner un fichier vidéo');
          }

          const formData = new FormData();
          formData.append('video', selectedFile);
          formData.append('title', videoForm.title);
          formData.append('description', videoForm.description);
          formData.append('projectId', projectId!);

          response = await fetch(url, {
            method,
            headers: {
              'Authorization': `Bearer ${adminToken}`,
            },
            body: formData,
          });
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erreur lors de la sauvegarde');
      }

      setShowVideoForm(false);
      setSelectedFile(null);
      // Recharger le projet
      handleAccess();
    } catch (err: any) {
      alert('Erreur lors de la sauvegarde: ' + err.message);
    }
  };

  if (!accessGranted) {
    return (
      <>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <Card className="w-full max-w-md">
            <h1 className="text-2xl font-bold text-center mb-6">Accès au projet</h1>
            <p className="text-gray-600 text-center mb-6">
              Entrez le code d'accès à 6 chiffres pour voir ce projet.
            </p>

            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

            <div className="text-center">
              <Button onClick={() => handleAccess()}>
                {loading ? 'Vérification...' : 'Actualiser l\'accès'}
              </Button>
            </div>
          </Card>
        </div>
        <ProjectAccessModal
          projectId={projectId!}
          isOpen={!accessGranted && !loading}
          onAccessGranted={handleAccessGranted}
        />
      </>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p>Chargement du projet...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">{project.name}</h1>
            {project.description && (
              <p className="text-gray-600 mt-2">{project.description}</p>
            )}
          </div>
          {isAdmin && (
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() => navigate('/admin')}
              >
                Retour sur tous les projets
              </Button>
              <Button onClick={handleAddVideo}>
                Ajouter une vidéo
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Modal pour ajouter/modifier une vidéo */}
      {showVideoForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingVideo ? 'Modifier la vidéo' : 'Ajouter une vidéo'}
            </h2>

            {/* Choix du mode (uniquement pour l'ajout) */}
            {!editingVideo && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Type de vidéo</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setVideoMode('youtube')}
                    className={`flex-1 py-2 px-4 rounded border ${
                      videoMode === 'youtube'
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    📺 Lien YouTube
                  </button>
                  <button
                    type="button"
                    onClick={() => setVideoMode('upload')}
                    className={`flex-1 py-2 px-4 rounded border ${
                      videoMode === 'upload'
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    📤 Upload fichier
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleVideoSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Titre</label>
                <input
                  type="text"
                  value={videoForm.title}
                  onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded"
                  required
                />
              </div>

              {!editingVideo && (
                <>
                  {videoMode === 'youtube' ? (
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">URL YouTube</label>
                      <input
                        type="url"
                        value={videoForm.youtubeUrl}
                        onChange={(e) => setVideoForm({...videoForm, youtubeUrl: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded"
                        placeholder="https://youtu.be/..."
                        required
                      />
                    </div>
                  ) : (
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">Fichier vidéo</label>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                        className="w-full p-2 border border-gray-300 rounded"
                        required
                      />
                      {selectedFile && (
                        <p className="text-sm text-gray-600 mt-1">
                          Fichier sélectionné : {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              {editingVideo && videoMode === 'youtube' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">URL YouTube</label>
                  <input
                    type="url"
                    value={videoForm.youtubeUrl}
                    onChange={(e) => setVideoForm({...videoForm, youtubeUrl: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="https://youtu.be/..."
                  />
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Description (optionnel)</label>
                <textarea
                  value={videoForm.description}
                  onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded"
                  rows={3}
                />
              </div>
              {editingVideo && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Version</label>
                    <input
                      type="number"
                      min="1"
                      value={videoForm.version}
                      onChange={(e) => setVideoForm({...videoForm, version: parseInt(e.target.value) || 1})}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Statut</label>
                    <select
                      value={videoForm.status}
                      onChange={(e) => setVideoForm({...videoForm, status: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="UPLOADING">Téléchargement</option>
                      <option value="PROCESSING">Traitement</option>
                      <option value="READY">Prêt</option>
                      <option value="DISABLED">Désactivé</option>
                      <option value="OBSOLETE">Obsolète</option>
                      <option value="FAILED">Échec</option>
                    </select>
                  </div>
                </>
              )}
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingVideo ? 'Modifier' : 'Ajouter'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowVideoForm(false)}
                >
                  Annuler
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Confirmer la suppression</h2>
            <p className="mb-6">Êtes-vous sûr de vouloir supprimer cette vidéo ?</p>
            <div className="flex gap-2">
              <Button
                variant="danger"
                onClick={confirmDeleteVideo}
                className="flex-1"
              >
                Supprimer
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setVideoToDelete(null);
                }}
                className="flex-1"
              >
                Annuler
              </Button>
            </div>
          </Card>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {project.videos.map((video) => (
            <Card key={video.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/videos/${video.id}`)} >
              <div>
                {video.thumbnailUrl && false  && (
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                )}
                <h3 className="font-semibold">{video.title}</h3>
                <div className="flex items-center gap-2 text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-white text-xs font-medium ${getVideoStatusClasses(video.status)}`}
                  >
                    {getVideoStatusLabel(video.status)}
                  </span>
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium">
                    v{video.version}
                  </span>
                  <span className="text-gray-500">
                    {video._count?.comments || 0} commentaires
                  </span>
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="secondary"
                    onClick={() => handleEditVideo(video)}
                    className="flex-1 text-xs py-1"
                  >
                    Modifier
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDeleteVideo(video.id)}
                    className="flex-1 text-xs py-1"
                  >
                    Supprimer
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>

        {project.videos.length === 0 && (
          <Card className="text-center py-12">
            <p className="text-gray-500">Aucune vidéo dans ce projet.</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PublicProject;
